import { getApolloClient } from '../lib/getApolloClient';
import { mapWithConcurrency } from '../util/concurrency.util';
import { handleGraphQLErrors } from '../util/graphqlError.util';
import type {
  ClientPeriodBill,
  InitiatePayRunForTenantRequest,
  MutationForceSyncPayRunCycleLogArgs,
  MutationInitiatePayRunForTenantArgs,
  PayRunCycleLogStatus,
  Query,
  QueryGetPayRunReadynessArgs,
} from '../generated/gql-types';
import {
  FORCE_SYNC_PAY_RUN_CYCLE_LOG,
  GET_CLIENT_PERIOD_BILL,
  GET_PAY_RUN_CYCLE_LOG,
  GET_PAY_RUN_CYCLE_LOGS,
  GET_PAY_RUN_READINESS,
  INITIATE_PAY_RUN_FOR_TENANT,
} from './payroll.gql';
import type { PayRunCycleLogWithReferences } from './payroll.type';

const uniqueIds = (ids: Array<string | null | undefined>): string[] =>
  Array.from(new Set(ids.filter((id): id is string => Boolean(id))));

/** Pay run readiness report as raw CSV text. */
const getPayRunReadiness = async (
  params: QueryGetPayRunReadynessArgs,
): Promise<string> => {
  const { data, errors } = await getApolloClient().query<
    Pick<Query, 'getPayRunReadyness'>,
    QueryGetPayRunReadynessArgs
  >({
    query: GET_PAY_RUN_READINESS,
    variables: params,
    fetchPolicy: 'network-only',
  });

  handleGraphQLErrors(errors, 'Unable to run pay run readiness.');

  return data?.getPayRunReadyness ?? '';
};

const getPayRunCycleLogs = async (params: {
  tenantId: string;
  periodStartISO: string;
  periodEndISO: string;
  status?: PayRunCycleLogStatus;
}): Promise<PayRunCycleLogWithReferences[]> => {
  const { data, errors } = await getApolloClient().query<{
    getPayRunCycleLogs?: Array<PayRunCycleLogWithReferences | null> | null;
  }>({
    query: GET_PAY_RUN_CYCLE_LOGS,
    variables: params,
    fetchPolicy: 'network-only',
  });

  handleGraphQLErrors(errors, 'Unable to load pay runs.');

  return (data?.getPayRunCycleLogs ?? []).filter(
    (log): log is PayRunCycleLogWithReferences => Boolean(log),
  );
};

const getPayRunCycleLog = async (
  id: string,
): Promise<PayRunCycleLogWithReferences | null> => {
  const { data, errors } = await getApolloClient().query<{
    getPayRunCycleLog?: PayRunCycleLogWithReferences | null;
  }>({
    query: GET_PAY_RUN_CYCLE_LOG,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  handleGraphQLErrors(errors, 'Unable to load cycle details.');

  return data?.getPayRunCycleLog ?? null;
};

const getClientPeriodBill = async (
  id: string,
): Promise<ClientPeriodBill | null> => {
  const { data, errors } = await getApolloClient().query<{
    getClientPeriodBill?: ClientPeriodBill | null;
  }>({
    query: GET_CLIENT_PERIOD_BILL,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  handleGraphQLErrors(errors, 'Unable to load client bill.');

  return data?.getClientPeriodBill ?? null;
};

/**
 * Fetches bills a few at a time; bills that fail to load are omitted.
 */
const getClientPeriodBillsByIds = async (
  ids: Array<string | null | undefined>,
): Promise<Record<string, ClientPeriodBill>> => {
  const results = await mapWithConcurrency(uniqueIds(ids), async (id) => {
    try {
      return await getClientPeriodBill(id);
    } catch {
      return null;
    }
  });

  return results.reduce<Record<string, ClientPeriodBill>>((acc, bill) => {
    if (bill?.id) {
      acc[bill.id] = bill;
    }
    return acc;
  }, {});
};

/**
 * Queues a pay run. Omitting the period falls back to the backend's default
 * window. The operator is emailed when the run finishes. The returned `id`
 * is the queue request id, NOT a cycle log id.
 */
const initiatePayRunForTenant = async (
  params: MutationInitiatePayRunForTenantArgs,
): Promise<InitiatePayRunForTenantRequest> => {
  const { data, errors } = await getApolloClient().mutate<
    { initiatePayRunForTenant?: InitiatePayRunForTenantRequest | null },
    MutationInitiatePayRunForTenantArgs
  >({
    mutation: INITIATE_PAY_RUN_FOR_TENANT,
    variables: params,
  });

  handleGraphQLErrors(errors, 'Unable to trigger pay run.');

  const result = data?.initiatePayRunForTenant;
  if (!result?.id) {
    throw new Error('Pay run was not queued.');
  }

  return result;
};

const forceSyncPayRunCycleLog = async (id: string): Promise<boolean> => {
  const { data, errors } = await getApolloClient().mutate<
    { forceSyncPayRunCycleLog?: boolean | null },
    MutationForceSyncPayRunCycleLogArgs
  >({
    mutation: FORCE_SYNC_PAY_RUN_CYCLE_LOG,
    variables: { id },
  });

  handleGraphQLErrors(errors, 'Unable to force sync this pay run cycle log.');

  return Boolean(data?.forceSyncPayRunCycleLog);
};

export const payrollActions = {
  getPayRunReadiness,
  getPayRunCycleLogs,
  getPayRunCycleLog,
  getClientPeriodBill,
  getClientPeriodBillsByIds,
  initiatePayRunForTenant,
  forceSyncPayRunCycleLog,
};
